import { useState, useEffect, useCallback, useMemo } from 'react';
import { BuildingOffice2Icon, ArrowPathIcon } from '@heroicons/react/24/outline';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathEmptyState,
  OathAlert,
  OathSpinner,
  OathAgencySelector,
  OathButton,
} from '../../shared/OathUI';
import { useOathAuth } from '../../../services/oathAuthContext';
import { getSubAgencyAR } from '../../../services/oathAgencyVitalsService';
import SubAgencyARTable from './SubAgencyARTable';
import { exportSubAgencyARCsv } from './exportSubAgencyAR';

const SubAgencyARPage = () => {
  const { user } = useOathAuth();
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);

  // Filter selector options to GAs only; fall back to all agencies if flag isn't available yet
  const gaAgencies = useMemo(() => {
    const gas = agencies.filter((a) => a.isGeneralAgent);
    return gas.length > 0 ? gas : agencies;
  }, [agencies]);

  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Auto-select first GA on load
  useEffect(() => {
    if (gaAgencies.length > 0 && !selectedAgencyId) {
      setSelectedAgencyId(gaAgencies[0].agencyId);
    }
  }, [gaAgencies, selectedAgencyId]);

  const fetchData = useCallback(async () => {
    if (!selectedAgencyId) return;
    setLoading(true);
    setError(null);
    const result = await getSubAgencyAR(selectedAgencyId);
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Failed to load sub-agency AR data.');
      setData(null);
    } else {
      setData(result.data);
    }
  }, [selectedAgencyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAgencyChange = (agencyId) => {
    setSelectedAgencyId(agencyId);
  };

  const handleExport = () => {
    if (data?.subAgencies?.length > 0) {
      exportSubAgencyARCsv(data.subAgencies, data.totals, selectedAgencyId);
    }
  };

  const subAgencyCount = data?.subAgencies?.length ?? 0;
  const subtitle = data && data.isGeneralAgent && subAgencyCount > 0
    ? `${subAgencyCount} active sub-agenc${subAgencyCount === 1 ? 'y' : 'ies'}`
    : 'Accounts receivable aging across all sub-agencies';

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[300px]">
          <OathSpinner size="lg" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="space-y-4">
          <OathAlert variant="error" message={error} />
          <div className="flex justify-center">
            <OathButton onClick={fetchData} variant="secondary">
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Retry
            </OathButton>
          </div>
        </div>
      );
    }

    if (!data) return null;

    if (data.subAgencies.length === 0) {
      return (
        <OathEmptyState
          icon={BuildingOffice2Icon}
          title="No Active Sub-Agencies"
          message="No active sub-agencies found for this agency."
        />
      );
    }

    return (
      <SubAgencyARTable
        subAgencies={data.subAgencies}
        totals={data.totals}
        onRefresh={fetchData}
        onExport={handleExport}
        loading={loading}
      />
    );
  };

  return (
    <OathPageWrapper maxWidth="max-w-7xl" responsive>
      <OathHeaderCard
        icon={BuildingOffice2Icon}
        title="Sub-Agency A/R"
        subtitle={subtitle}
        rightContent={agencies.length > 1 ? (
          <OathAgencySelector
            agencies={gaAgencies}
            selectedAgencyId={selectedAgencyId}
            onSelect={handleAgencyChange}
          />
        ) : gaAgencies.length === 1 ? (
          <span className="text-sm qhd:text-base text-gray-500 dark:text-gray-400">
            {gaAgencies[0].dbaAgencyName} <span className="text-xs text-gray-400 dark:text-gray-500">#{gaAgencies[0].agencyId}</span>
          </span>
        ) : undefined}
      />

      {renderContent()}
    </OathPageWrapper>
  );
};

export default SubAgencyARPage;
